import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import { sanitizeStaffReturnPath } from '../../utils/authReturnUrl';
import { buildCustomerLoginUrl } from '../../utils/authReturnUrl';
import { wsPath } from '../../utils/siteHosts';
import LoginForm from './LoginForm';
import MfaForm from './MfaForm';
import SignedInPanel from './SignedInPanel';
import { authErrorClass, authLinkClass } from './authFormStyles';

type StaffAuthPanelProps = {
  returnTarget?: string | null;
};

function StaffAuthPanel({ returnTarget }: StaffAuthPanelProps) {
  const { isAuthenticated, auth } = useAuth();
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [error, setError] = useState('');

  useEffect(() => {
    if (!isAuthenticated) return;

    if (auth.type === 'CUSTOMER') {
      window.location.assign(buildCustomerLoginUrl('/account'));
      return;
    }

    const isShippingAgent = auth.type === 'SHIPPING_AGENT';
    const isStaffType =
      auth.type === 'ADMIN' ||
      auth.type === 'EMPLOYEE' ||
      isShippingAgent;

    if (!isStaffType) {
      navigate(wsPath('/workspace'), { replace: true });
      return;
    }

    const rawResume = returnTarget ? sanitizeStaffReturnPath(returnTarget) : null;
    const defaultDestination = isShippingAgent
      ? wsPath('/workspace/delivery/scan')
      : wsPath('/workspace');

    navigate(rawResume ?? defaultDestination, { replace: true });
  }, [auth.type, isAuthenticated, navigate, returnTarget]);

  if (isAuthenticated) {
    return <SignedInPanel />;
  }

  if (auth.mfaRequired) {
    return (
      <div className="space-y-5">
        <h2 className="text-base font-semibold tracking-tight text-slate-900">
          {t('auth.mfa_title')}
        </h2>
        {error ? <p className={authErrorClass('light')}>{error}</p> : null}
        <MfaForm onError={setError} tone="light" />
      </div>
    );
  }

  const inputTone = 'light' as const;

  return (
    <div>
      {auth.requiresEmailVerification ? (
        <p className="mb-3 rounded-sm border border-amber-200 bg-amber-50 px-3 py-2 text-xs text-amber-900">
          {t('auth.verify_email_resent')}
        </p>
      ) : null}
      {error ? <p className={`mb-3 ${authErrorClass('light')}`}>{error}</p> : null}
      <LoginForm
        tone={inputTone}
        onMfaRequired={() => {}}
        onError={(msg) => setError(msg)}
      />
      <div className="mt-4 flex flex-wrap gap-x-4 gap-y-1 border-t border-slate-200 pt-4">
        <button
          type="button"
          onClick={() => navigate('/forgot-password')}
          className={authLinkClass('light')}
        >
          {t('auth.forgot_link', 'Forgot your password?')}
        </button>
        <button
          type="button"
          onClick={() => navigate('/recover-account')}
          className={authLinkClass('light')}
        >
          {t('auth.use_recovery_code', { defaultValue: 'Use a recovery code' })}
        </button>
      </div>
    </div>
  );
}

export default StaffAuthPanel;
